import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  passPrintTemplate,
  toastUtils, useDeviceSupport
} from '@/utils/index.jsx'
import { useTranslation } from 'react-i18next'
import { PowerIcon, PowerOff, Printer, ZapIcon, ZapOffIcon } from 'lucide-react'
import { Button } from 'devextreme-react/button'
import { renderToStaticMarkup } from 'react-dom/server'
import { usePage } from '@inertiajs/react'
import ReceiptPrinterEncoder from '@point-of-sale/receipt-printer-encoder'
// import WebUSBReceiptPrinter from '@point-of-sale/webusb-receipt-printer'
// import WebSerialReceiptPrinter from '@point-of-sale/webserial-receipt-printer'
// import WebBluetoothReceiptPrinter from '@point-of-sale/webbluetooth-receipt-printer'
import { Col, Row } from '../Grid/index.jsx'
import { DevExLookup } from '../DevExtreme/index.jsx'
import { Modal } from '@/components/Shared/Modal.jsx'
import { AppCtx } from '../providers/AppProvider.jsx'
import { ThermalDeviceAPI } from '../../printerService/index.jsx'

// const PRINTER_CONNECTIONS = [
//   { key: 'serial', value: 'Serial' },
//   { key: 'usb', value: 'USB' },
//   { key: 'bluetooth', value: 'Bluetooth' }
//   // { key: 'network', value: 'Network' }
// ]

const PRINTER_BAUD_RATES = [
  { key: 9600, value: '9600' },
  { key: 38400, value: '38400' },
  { key: 115200, value: '115200' }
]

const PRINTER_COLUMNS = [
  { key: 32, value: '57mm 32 characters' },
  { key: 35, value: '57mm 35 characters' },
  { key: 42, value: '80mm 42 characters' },
  { key: 44, value: '80mm 44 characters' },
  { key: 48, value: '80mm 48 characters' }
]

const PRINTER_MODELS = ReceiptPrinterEncoder.printerModels
PRINTER_MODELS.unshift({ id: 'generic', name: 'Generic' })

// let printerInstance

const thermalDeviceApi = new ThermalDeviceAPI({
  printerPortKey: 'thermalPrinterPort'
})

export const useThermalDevice = () => {
  const { t } = useTranslation()
  const { company } = usePage().props
  const modalRef = useRef(null)
  const { startLoading, endLoading } = useContext(AppCtx)

  const [isConnected, setIsConnected] = useState(false)

  const thermalPrinterSetupSaved = window.localStorage.getItem('thermalPrinterSetup')
  const thermalPrinterSetupSavedParsed = thermalPrinterSetupSaved ? JSON.parse(thermalPrinterSetupSaved) : null

  const [thermalPrinterSetup, setThermalPrinterSetup] = useState(thermalPrinterSetupSavedParsed ?? {
    connection: 'serial',
    model: 'generic',
    baudrate: 9600,
    columns: 48
  })

  useEffect(() => {
    window.localStorage.setItem('thermalPrinterSetup', JSON.stringify(thermalPrinterSetup))
  }, [thermalPrinterSetup])

  // const getPrinterInstance = (thermalPrinterSetupLocal) => {
  //   switch (thermalPrinterSetupLocal.connection) {
  //     case 'usb':
  //       return new WebUSBReceiptPrinter()
  //     case 'bluetooth':
  //       return new WebBluetoothReceiptPrinter()
  //     case 'serial':
  //     default:
  //       return new WebSerialReceiptPrinter({
  //         baudRate: parseInt(thermalPrinterSetupLocal.baudrate, 10)
  //       })
  //   }
  // }

  const getEncoder = (thermalPrinterSetupLocal) => {
    const encoderOptions = {
      columns: thermalPrinterSetupLocal.columns
    }

    if (thermalPrinterSetupLocal.model !== 'generic') {
      encoderOptions.printerModel = thermalPrinterSetupLocal.model
    }

    return new ReceiptPrinterEncoder(encoderOptions)
  }

  const thermalDevice = {
    autoReconnect: () => {
      if (!thermalDeviceApi.isSerialPortOpen() && !isConnected) {
        thermalDeviceApi.getSavedSerialPort()
          .then(res => {
            if (res) {
              setIsConnected(true)
            }
          })
      }
    },
    isConnected,
    setIsConnected,
    api: thermalDeviceApi,
    async handlePrintPass (accessPass) {
      startLoading()

      const encoder = getEncoder(thermalPrinterSetup)

      const data = await passPrintTemplate(encoder, t, company, accessPass)

      try {
        await thermalDeviceApi.print(data.cut().encode())

        toastUtils.success(`${t('labels.thermal_printer')} - ${t('messages.processed_successfully')}`)

        setIsConnected(true)
      } catch (error) {
        toastUtils.error(error)
      }

      endLoading()
    },
    printConnection () {
      return (
        <>
          <Button
            className='sm:ml-2 mb-2 sm:mb-0 w-full sm:w-auto'
            icon={renderToStaticMarkup(
              this.isConnected
                ? <PowerIcon size={14} className='inline-block' color='#10b981' />
                : <PowerOff size={14} className='inline-block' color='#d12f0f' />
            )}
            text={t('labels.thermal_printer')}
            stylingMode='outlined'
            onClick={() => {
              modalRef.current.open()
              modalRef.current.setInnerData({
                title: t('labels.thermal_printer'),
                body: (
                  <ThermalDeviceModal
                    thermalPrinterSetup={thermalPrinterSetup}
                    setThermalPrinterSetup={setThermalPrinterSetup}
                    isConnected={isConnected}
                    setIsConnected={setIsConnected}
                    thermalDevice={this}
                    getEncoder={getEncoder}
                  />
                ),
                modalProps: {
                  width: 600,
                  hideConfirmButton: true
                }
              })
            }}
          />

          <Modal ref={modalRef} />
        </>
      )
    }
  }

  useEffect(() => {
    thermalDevice.autoReconnect()
  }, [])

  return { thermalDevice }
}

const ThermalDeviceModal = ({
  thermalPrinterSetup,
  setThermalPrinterSetup,
  isConnected,
  setIsConnected,
  thermalDevice,
  getEncoder
}) => {
  const { t } = useTranslation()

  const [isConnectedLocal, setIsConnectedLocal] = useState(isConnected)
  const [thermalPrinterSetupLocal, setThermalPrinterSetupLocal] = useState(thermalPrinterSetup)

  const validationGroup = 'thermal-printer'
  const errors = []

  useEffect(() => {
    setThermalPrinterSetup(thermalPrinterSetupLocal)
  }, [thermalPrinterSetupLocal])

  useEffect(() => {
    setIsConnected(isConnectedLocal)
  }, [isConnectedLocal])

  const onTestPrint = async () => {
    try {
      const encoder = getEncoder(thermalPrinterSetupLocal)

      // const data = encoder
      //   .initialize()
      //   .codepage('auto')
      //   .text('Test print')
      //   .newline()
      //   .text('Тест кирилица')
      //   .newline()
      //   .bold()
      //   .text('bold')
      //   .newline(2)
      //   .align('center')
      //   .invert(true)
      //   .height(3)
      //   .text('CREAM')
      //   .invert(false)
      //   .newline()
      //   .rule({ style: 'double' })
      //   .newline()
      //   .qrcode('https://app.cream.mk')
      //   .newline()
      //   .rule({ style: 'double' })
      //   .newline(2)
      //   .box(
      //     { align: 'center', style: 'single' },
      //     'The quick brown fox jumps over the lazy dog'
      //   )
      //   .newline(2)
      //   .barcode('313063057461', 'ean13', {
      //     width: 3
      //   })
      //   .newline(2)
      //   .pdf417('https://app.cream.mk')
      //   .newline(2)
      //   .line('Thank you for using CREAM')

      const data = encoder.initialize()
        .codepage('auto')
        .text(t('messages.printer_connected'))
        .cut()
        .encode()
      /* Print the receipt */
      thermalDevice.api.print(data)
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Row>
      {/* <Col> */}
      {/*  <DevExLookup */}
      {/*    dataField='connection' */}
      {/*    label={t('labels.connection')} */}
      {/*    errors={errors} */}
      {/*    validationGroup={validationGroup} */}
      {/*    setData={(dataField, value) => { */}
      {/*      setThermalPrinterSetupLocal({ */}
      {/*        ...thermalPrinterSetupLocal, */}
      {/*        connection: value */}
      {/*      }) */}
      {/*    }} */}
      {/*    value={thermalPrinterSetupLocal.connection} */}
      {/*    dataSource={PRINTER_CONNECTIONS} */}
      {/*    showClearButton={false} */}
      {/*    valueExpr='key' */}
      {/*    displayExpr='value' */}
      {/*    disabled={isConnectedLocal} */}
      {/*    escapeIsLocked */}
      {/*  /> */}
      {/*  /!* <p className='mb-4 text-sm font-bold'>Support for {thermalPrinterSetup.connection}: {useDeviceSupport(thermalPrinterSetup.connection) ? 'Yes' : 'No'}</p> *!/ */}
      {/* </Col> */}
      <Col>
        <DevExLookup
          dataField='model'
          label={t('labels.model')}
          errors={errors}
          validationGroup={validationGroup}
          setData={(dataField, value) => {
            setThermalPrinterSetupLocal({
              ...thermalPrinterSetupLocal,
              model: value
            })
          }}
          value={thermalPrinterSetupLocal.model}
          dataSource={PRINTER_MODELS}
          showClearButton={false}
          valueExpr='id'
          displayExpr='name'
          disabled={isConnectedLocal}
          escapeIsLocked
        />
      </Col>
      {thermalPrinterSetupLocal.connection === 'serial' && (
        <Col>
          <DevExLookup
            dataField='baudrate'
            label={t('labels.speed')}
            errors={errors}
            validationGroup={validationGroup}
            setData={(dataField, value) => {
              setThermalPrinterSetupLocal({
                ...thermalPrinterSetupLocal,
                baudrate: value
              })
            }}
            value={thermalPrinterSetupLocal.baudrate}
            disabled={isConnectedLocal}
            dataSource={PRINTER_BAUD_RATES}
            showClearButton={false}
            valueExpr='key'
            displayExpr='value'
            escapeIsLocked
          />
        </Col>
      )}
      <Col>
        <DevExLookup
          dataField='columns'
          label={t('labels.paper_width')}
          errors={errors}
          validationGroup={validationGroup}
          setData={(dataField, value) => {
            setThermalPrinterSetupLocal({
              ...thermalPrinterSetupLocal,
              columns: value
            })
          }}
          value={thermalPrinterSetupLocal.columns}
          dataSource={PRINTER_COLUMNS}
          showClearButton={false}
          valueExpr='key'
          displayExpr='value'
          disabled={isConnectedLocal}
          escapeIsLocked
        />
      </Col>
      <Col>
        <Button
          icon={renderToStaticMarkup(
            !isConnectedLocal
              ? <ZapIcon size={14} />
              : <ZapOffIcon size={14} />
          )}
          text={!isConnectedLocal ? t('ui.connect') : t('ui.disconnect')}
          stylingMode='outlined'
          onClick={() => {
            if (!thermalDevice.api.isSerialPortOpen() && !isConnectedLocal) {
              thermalDevice.api.connect()
                .then(() => {
                  setIsConnectedLocal(true)
                })
                .catch(error => {
                  toastUtils.error(error)
                })
            } else {
              thermalDevice.api.disconnect({ deleteSavedConnection: true })
              setIsConnectedLocal(false)
            }
          }}
          disabled={!useDeviceSupport(thermalPrinterSetupLocal.connection)}
        />
        <Button
          className='ml-4'
          icon={renderToStaticMarkup(<Printer size={14} />)}
          text={t('ui.test_print')}
          stylingMode='outlined'
          onClick={onTestPrint}
          disabled={!isConnectedLocal}
        />
      </Col>
    </Row>
  )
}
